
<script setup>
import { useHead } from '@unhead/vue';
import OgImage from '../../images/blog/1-percent-2025/1ftp-1.png';

useHead({
	"title": "Swagger’s 1% for the Planet Impact in 2025",
	"meta": [
		{
			"name": "description",
			"content": "Swagger’s 1% for the Planet commitment in 2025. Learn about the nonprofits we’re supporting and how we’re contributing to a more sustainable future."
		},
		{
			"rel": "canonical",
			"href": "https://giveswagger.com/swagger-1-percent-for-the-planet-2025"
		},
		{
			"property": "og:title",
			"content": "Swagger’s 1% for the Planet Impact in 2025"
		},
		{
			"property": "og:description",
			"content": "Swagger’s 1% for the Planet commitment in 2025. Learn about the nonprofits we’re supporting and how we’re contributing to a more sustainable future."
		},
		{
			"property": "og:url",
			"content": "https://giveswagger.com/swagger-1-percent-for-the-planet-2025"
		},
		{
			"property": "og:image",
			"content": `https://giveswagger.com${OgImage}`
		}
	]
});
</script>
<template lang="pug">

article.page.page--simple
	header.page__header
		h1 Swagger’s 1% for the Planet Impact: Our 2025 Journey of Giving Back
	.page__content
		section.section
			.section__content
				.featured
					a(href="https://www.onepercentfortheplanet.org/?_gl=1*1ya5n7p*_up*MQ..*_gs*MQ..&gclid=CjwKCAjwwqfABhBcEiwAZJjC3ip3-SjQfWofxIwTrKHS1wWzRWzMZhQTvR-s5v_SGn5HA3B9wTdtiRoCnPwQAvD_BwE&gbraid=0AAAAAqnql3NxD9_Ps4KATaNJLgk5EZMmI" target="_blank")
						img(src='../../images/blog/1-percent-2025/1ftp-1.png' alt='Ocean wave with the 1% for the Planet logo and the message “All together for the planet,” symbolizing collective environmental action.')
				
				p At Swagger, sustainability is more than just a buzzword—it’s a core part of who we are. As proud members of <a href="https://www.onepercentfortheplanet.org/?_gl=1*1ya5n7p*_up*MQ..*_gs*MQ..&gclid=CjwKCAjwwqfABhBcEiwAZJjC3ip3-SjQfWofxIwTrKHS1wWzRWzMZhQTvR-s5v_SGn5HA3B9wTdtiRoCnPwQAvD_BwE&gbraid=0AAAAAqnql3NxD9_Ps4KATaNJLgk5EZMmI" target="_blank">1% for the Planet</a>, we’ve continued our commitment to donate 1% of annual sales to environmental nonprofits making real change.

				p In recent years, we’ve brought our team into the process—handpicking nonprofits that reflect the causes closest to their hearts. From protecting wildlife to restoring coral reefs, each organization we support represents a unique mission that’s shaping a better future for our planet. We’re excited to share the incredible nonprofits we’re backing in 2025, starting with a few of our team’s standout picks. Let’s dive in!

				h2 Featured Nonprofits We’re Supporting in 2025:
				h3 1. Baykeeper: Protecting the Bay
				img(src='../../images/blog/1-percent-2025/1ftp-2.png')
				p <a target="_blank" href="https://baykeeper.org/">Baykeeper</a> is fighting pollution in San Francisco Bay, working to ensure the water remains clean and healthy for future generations.
				p <i>Why It Matters:</i> By addressing water quality issues head-on, Baykeeper is making sure that one of the world’s most iconic bays remains a thriving habitat for wildlife and people alike.

				h3 2. Natural Resources Defense Council (NRDC): Powering Policy Change
				img(src='../../images/blog/1-percent-2025/1ftp-3.png')
				p <a target="_blank" href="https://www.nrdc.org/">NRDC</a> tackles environmental issues on a global scale, from fighting climate change to protecting wildlife. Their work shapes policy at the highest levels.
				p <i>Why It Matters:</i> NRDC is using its legal expertise to push for major policy changes that protect the environment, ensuring that our planet stays healthy for generations to come.

				h3 3. Urban Roots: Growing Greener Communities
				img(src='../../images/blog/1-percent-2025/1ftp-4.png')
				p <a target="_blank" href="https://urbanrootsatx.org/">Urban Roots</a> empowers communities through urban farming, fostering leadership and sustainability while addressing food insecurity.
				p <i>Why It Matters:</i> Urban Roots creates opportunities for hands-on learning, sustainable agriculture, and building stronger, more resilient communities.

				h3 4. Reef Guardians: Saving Coral Reefs
				img(src='../../images/blog/1-percent-2025/1ftp-5.png')
				p <a target="_blank" href="https://www.reefguardians.org/">Reef Guardians</a> works to restore and protect coral reefs, ensuring that these vital marine ecosystems can thrive in the face of climate change.
				p <i>Why It Matters:</i> Coral reefs are vital for ocean biodiversity, and Reef Guardians is dedicated to their protection through education, restoration, and scientific research.

				h3 5. Humane World: Protecting Wildlife
				img(src='../../images/blog/1-percent-2025/1ftp-6.png')
				p <a target="_blank" href="https://www.humaneworld.org/en">Humane World</a> advocates for the welfare of animals globally, supporting sustainable practices that ensure wildlife is treated with respect and compassion.
				p <i>Why It Matters:</i> Humane World’s work is making the planet more sustainable and ethical, pushing for a world where both animals and ecosystems are protected.

				h2 More Incredible Nonprofits We’re Supporting:

				h3 Lindsay Wildlife Experience:
				p <a target="_blank" href="https://lindsaywildlife.org/">Lindsay Wildlife</a> provides wildlife rehabilitation and education, teaching the public about the importance of coexisting with wildlife.

				h3 California Wildlife Center (CWC):
				p <a target="_blank" href="https://cawildlife.org/">CWC</a> rescues and rehabilitates injured animals with a focus on returning them to their natural habitats, ensuring the survival of California’s diverse wildlife.

				h3 Ocean Foundation:
				p <a target="_blank" href="https://oceanfdn.org/">Ocean Foundation</a> is committed to marine conservation and protecting ocean ecosystems through funding, education, and advocacy.

				h3 1000 Friends of Oregon:
				p <a target="_blank" href="https://friends.org/">1000 Friends of Oregon</a> is a nonprofit advocacy organization working to protect Oregon’s farms, forests, and natural areas while promoting sustainable, equitable land use and smart community development across the state.

				h3 Sustainable Organic Integrated Livelihoods (SOIL):
				p <a target="_blank" href="https://www.oursoil.org/">SOIL</a> works in Haiti to promote sustainable agricultural practices and improve soil health while providing economic opportunities to local communities.

				h3 Walking Mountains Science Center:
				p <a target="_blank" href="https://www.walkingmountains.org/">Walking Mountains</a> provides environmental education programs that inspire individuals to make a positive impact on the world around them.

				h3 National Parks Conservation Association (NPCA):
				p <a target="_blank" href="https://www.npca.org/">NPCA</a> is dedicated to preserving and protecting America’s national parks, ensuring these natural treasures are maintained for future generations to enjoy.

				h3 Marine Conservation Institute:
				p <a target="_blank" href="https://marine-conservation.org/">Marine Conservation Institute</a> is a nonprofit organization dedicated to protecting marine ecosystems through science-based advocacy, the creation of marine protected areas, and strategic partnerships that promote ocean conservation worldwide.

				h3 Save the Sharks:
				p <a target="_blank" href="https://www.savethesharksorg.com/">Save the Sharks</a> is a nonprofit organization focused on shark conservation through education, outreach, and advocacy, aiming to dispel misconceptions and protect shark species and their ocean habitats.

				h3 The New England Women in Energy and the Environment:
				p <a target="_blank" href="https://newiee.org/">NEWIEE</a> is a nonprofit organization that fosters a community of women leaders across the energy and environmental sectors through networking, professional development, and mentorship.

				h3 Explore Ecology:
				p <a target="_blank" href="https://exploreecology.org/">Explore Ecology</a> is an environmental education nonprofit that inspires children and adults to care for the Earth through hands-on learning about composting, waste reduction, art, and environmental stewardship.

				h3 Planting Justice:
				p <a target="_blank" href="https://plantingjustice.org/">Planting Justice</a> is a grassroots organization working to transform the food system by creating green jobs, building community gardens, and providing fresh, healthy food in underserved areas.

				h3 Veterans Outdoor Advocacy Group:
				p <a target="_blank" href="https://www.voag.org/">VOAG</a> promotes the benefits of outdoor adjunct therapy for veterans through research, advocacy, and increased access—while supporting the well-being of our public lands.

				h3 Philadelphia Orchard Project:
				p <a target="_blank" href="https://www.phillyorchards.org/">The Philadelphia Orchard Project</a> plants and supports community orchards in the city, expanding access to fresh fruit, promoting food justice, and cultivating greener urban spaces.

				h2 Continuing the Journey
				img(src='../../images/blog/1-percent-2025/1ftp-7.png')

				p We’re so proud of the nonprofits our team has chosen to support this year. Each one is doing incredible work to make a real difference, from protecting wildlife to advocating for environmental justice. By supporting these organizations, we’re helping to drive positive change on a global scale.
				p At Swagger, our 1% for the Planet commitment isn’t just about writing a check—it’s about being an active part of the solution. We’re excited for the future and ready to continue our journey with 1% for the Planet. Here’s to making the world a better place, together. 🌍💚
				p Want to be a part of this journey with us? Check out our sustainable swag and join us in making an impact! Just fill out this short form: <router-link to="/get-started">Let’s Do This!</router-link>

				p We’d love to hear from you! Drop us a note anytime at <a href="mailto:hello@giveswagger.com">hello@giveswagger.com</a>

				
</template>
  